import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import { routes } from "../../configs/constants/routes";
import { useSendTwoFactorCode } from "../../networking/internal-user.hooks";
import OtpInput from "react-otp-input";
import { Flex } from "@chakra-ui/react";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import { sendCodeRequest } from "../../models/InternalUser";
import { toast } from "../../utils/toast";
import {
	clearSessionExpiredNotice,
	hasSessionExpiredNotice,
	useAuthStore,
} from "../../stores/auth.store";

type Inputs = {
	email: string;
	password: string;
};

const LoginPage: React.FC = () => {
	const authStore = useAuthStore();
	const { mutateAsync: sendCode } = useSendTwoFactorCode();
	const { register, handleSubmit } = useForm<Inputs>({
		defaultValues: {
			email: "",
			password: "",
		},
	});
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [loginFailed, setLoginFailed] = useState(false);
	const [otpEnabled, setOtpEnabled] = useState(false);
	const [otp, setOtp] = useState("");
	const [otpError, setOtpError] = useState<boolean>(false);
	const [otpErrorMessage, setOtpErrorMessage] = useState<string>("");
	const [verificationId, setVerificationId] = useState("");
	const [timer, setTimer] = useState(60); // 60 seconds countdown
	const [isDisabled, setIsDisabled] = useState(true);
	const [phoneNumber, setPhoneNumber] = useState("");
	// Read once: the flag is cleared right away so a reload or a later visit
	// does not show it again, but the notice stays until the user logs in.
	const [sessionExpired] = useState(hasSessionExpiredNotice);

	useEffect(() => {
		clearSessionExpiredNotice();
	}, []);

	const [loginErrorKey, setLoginErrorKey] = useState<"login.failed" | "login.twoFactorPhoneMissing">(
		"login.failed"
	);

	const onSubmit = handleSubmit(async ({ email, password }) => {
		const res = await authStore.authenticate(email, password);

		if (res && typeof res === "object" && "verificationId" in res && "ttl" in res) {
			setOtpEnabled(true);
			setVerificationId(res.verificationId);
			setPhoneNumber(res.phoneNumber);
			setTimer(parseInt(res?.ttl || "60000") / 1000 || 60);
			setIsDisabled(true);
		} else if (res && typeof res === "object" && "errorCode" in res) {
			setLoginFailed(true);
			setLoginErrorKey(
				res.errorCode === "MISSING_PHONE_VALUE_FOR_TFA"
					? "login.twoFactorPhoneMissing"
					: "login.failed"
			);
		} else {
			setLoginFailed(!res);
			setLoginErrorKey("login.failed");
		}
	});

	const navigateToPasswordReset = () => {
		navigate(routes.FORGOT_PASSWORD);
	};

	async function validate2FA() {
		if (otp && verificationId) {
			const res = await authStore.authenticateSms(otp, verificationId);
			setOtpError(!res);
			if (!res) {
				setOtpErrorMessage(t("otp2FA.otpErrorMessage"));
			}
		} else {
			setOtpError(true);
			setOtpErrorMessage(t("otp2FA.otpEmptyErrorMessage"));
		}
	}
	async function reSendOtpSms() {
		if (phoneNumber) {
			const sendCodeRequest: sendCodeRequest = {
				phone: phoneNumber,
			};
			const otpResult: any = await sendCode(sendCodeRequest).catch((e) => e);
			if (otpResult.verificationId) {
				toast.success(FEEDBACK_MESSAGE_TYPE.RESEND_SUCCESSFUL);
				setVerificationId(otpResult.verificationId);
				if (otpResult.verificationId) {
					setTimer(parseInt(otpResult?.ttl || "60000") / 1000 || 60);
					setIsDisabled(true);
				}
			} else {
				toast.error(FEEDBACK_MESSAGE_TYPE.RESEND_FAILED);
			}
		}
	}

	useEffect(() => {
		let interval: any;
		if (isDisabled) {
			interval = setInterval(() => {
				setTimer((prev) => {
					if (prev === 1) {
						clearInterval(interval);
						setIsDisabled(false);
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}
		return () => clearInterval(interval);
	}, [isDisabled]);

	const renderLoginForm = () => {
		return (
			<LoginContainer>
				<AppText
					margin="2rem"
					letterSpacing="0.24rem"
					color="grayText"
					size="l"
					textTransform="uppercase"
				>
					Dashboard
				</AppText>
				<FormComponent onSubmit={onSubmit}>
					{sessionExpired && !loginFailed && (
						<div
							role="status"
							className="mb-4 w-full rounded-md bg-white/70 px-3 py-2 text-center text-sm text-gray-700"
						>
							{t("login.sessionExpired")}
						</div>
					)}
					<AppInput
						{...register("email")}
						margin="0.5rem"
						containerMargin="0"
						placeholder="login.email"
						icon="email"
					/>
					<AppInput
						{...register("password")}
						margin="0.5rem"
						containerMargin="0"
						placeholder="login.password"
						icon="password"
						type="password"
					/>
					{loginFailed && <AppText color="danger">{t(loginErrorKey)}</AppText>}
					<Button margin="2rem">{t("login.login")}</Button>
				</FormComponent>
				<ForgotpasswordWrapper onClick={() => navigateToPasswordReset()}>
					<AppText size="s" color="grayText">
						{t("login.haveYouForgottenPassword")}
					</AppText>
				</ForgotpasswordWrapper>
			</LoginContainer>
		);
	};

	const renderOtpForm = () => {
		return (
			<LoginContainerOtp>
				<AppText
					margin="2rem"
					letterSpacing="0.24rem"
					color="grayText"
					size="l"
					textTransform="uppercase"
				>
					Dashboard
				</AppText>

				<AppText margin="1rem" color="grayText" size="s" textAlign="center">
					{t("otp2FA.loginScreenMessage")}
				</AppText>

				<Flex
					direction="row"
					justifyContent="center"
					alignItems="center"
					marginTop={"12px"}
					marginBottom={"6px"}
				>
					<OtpInput
						value={otp}
						onChange={setOtp}
						numInputs={6}
						renderSeparator={<span> </span>}
						renderInput={(props) => <input {...props} />}
						inputStyle={{
							width: "50px",
							height: "50px",
							margin: "0 5px",
							fontSize: "20px",
							borderRadius: "4px",
							border: "0px solid gray",
							textAlign: "center",
							outline: "none",
						}}
					/>
				</Flex>
				{otpError && (
					<Flex
						direction={"row"}
						alignItems={"center"}
						justifyContent={"center"}
						marginTop={"22px"}
						marginBottom={"1px"}
					>
						<AppText color="danger">{otpErrorMessage}</AppText>
					</Flex>
				)}

				<Button onClick={() => validate2FA()} margin="2rem">
					{t("login.login")}
				</Button>

				{isDisabled ? (
					<p style={{ fontSize: "12px", color: "#000" }}>
						{t("otp2FA.resendCodeMessage")}
						{` `} {timer} {` `} {t("otp2FA.second")}
					</p>
				) : (
					<p style={{ fontSize: "12px", color: "#000" }}>
						{t("otp2FA.resendCodeRemaining")}{" "}
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								reSendOtpSms();
							}}
							style={{
								color: "#3498db",
								textDecoration: "underline",
								cursor: "pointer",
							}}
						>
							{t("otp2FA.sendNewCode")}
						</a>
					</p>
				)}
			</LoginContainerOtp>
		);
	};

	if (authStore.isLoggedIn) {
		return <Navigate replace to={routes.SCHEDULE} />;
	} else return !otpEnabled ? renderLoginForm() : renderOtpForm();
};

export default LoginPage;

const FormComponent = styled.form`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 270px;
`;

const LoginContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: ${(props) => props.theme.colors.COLOR_DARK_GRAY};
	box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.1);
	border-radius: 5px;
	width: 25.625rem;
	padding: 1rem 0 2rem;
	margin-bottom: 5vh; /* to push login box up slightly above vertical center */
`;

const LoginContainerOtp = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: ${(props) => props.theme.colors.COLOR_DARK_GRAY};
	box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.1);
	border-radius: 5px;
	width: 29.625rem;
	padding: 1rem 0 2rem;
	margin-bottom: 5vh; /* to push login box up slightly above vertical center */
`;

const ForgotpasswordWrapper = styled.div`
	cursor: pointer;
`;
